import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Analytics } from './pages/Analytics';
import { Users } from './pages/Users';
import { UserDetail } from './pages/UserDetail';
import { Billing } from './pages/Billing';
import { Twins } from './pages/Twins';
import { TwinDetail } from './pages/TwinDetail';
import { Settings } from './pages/Settings';
import { DemoUsers } from './pages/DemoUsers';
import { Email } from './pages/Email';
import { BestPractices } from './pages/BestPractices';
import { BlogPress } from './pages/BlogPress';
import { BlogPressAccessRequests } from './pages/BlogPressAccessRequests';
import { AccessDenied } from './pages/AccessDenied';

/**
 * `requireAdmin` defaults to true, which preserves the exact previous
 * behavior (every route was admin-only in practice, since there was no
 * other signed-in role) for every existing route without needing to touch
 * them. Only /blog-press passes `requireAdmin={false}` to additionally
 * admit approved contributors, matching the backend's own
 * blog_press_manage_required on those article endpoints.
 */
function ProtectedRoute({
  children,
  requireAdmin = true,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) {
  const { user, loading, role, roleLoading } = useAuth();

  if (loading || (user && roleLoading)) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role === 'none') {
    return <AccessDenied />;
  }

  if (requireAdmin && role !== 'admin') {
    // A contributor hitting an admin-only route: send them to the one page
    // they're allowed on, rather than an access-denied dead end — this is
    // the expected everyday case (a contributor just signed in), not an
    // error condition.
    return <Navigate to="/blog-press" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:userId"
        element={
          <ProtectedRoute>
            <UserDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/twins"
        element={
          <ProtectedRoute>
            <Twins />
          </ProtectedRoute>
        }
      />
      <Route
        path="/twins/:twinId"
        element={
          <ProtectedRoute>
            <TwinDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/demo-users"
        element={
          <ProtectedRoute>
            <DemoUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute>
            <Email />
          </ProtectedRoute>
        }
      />
      <Route
        path="/best-practices"
        element={
          <ProtectedRoute>
            <BestPractices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/blog-press"
        element={
          <ProtectedRoute requireAdmin={false}>
            <BlogPress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/blog-press-access-requests"
        element={
          <ProtectedRoute>
            <BlogPressAccessRequests />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
